import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { ApiError } from "@/core/api/client";

import { useSession } from "./useSession";

/**
 * Adaptateur entre la session server-side du cockpit et le contrat attendu
 * par Convex (`ConvexProviderWithAuth`). Convex appelle `fetchAccessToken`
 * pour recuperer un JWT WorkOS frais a attacher a chaque requete, et lit
 * `isAuthenticated`/`isLoading` pour `<Authenticated>/<Unauthenticated>/<AuthLoading>`.
 *
 * `getAccessToken` ici hit `/api/auth/jwt` cote Cyrano qui retourne le JWT
 * stocke server-side (refresh automatique si expire). Le browser ne stocke
 * jamais ce JWT entre 2 calls.
 *
 * 🪤 **Rendre `null` ici DÉCONNECTE le chatteur, définitivement.** Ce n'est
 * pas « pas de token cette fois » : côté Convex, un seul `null` fait
 * `clearAuth()` + `setAuthState({state: "noAuth"})`, et `noAuth` est un
 * cul-de-sac — `refetchToken()` comme `tryToReauthenticate()` en sortent par
 * un early-return, et le seul chemin de retour est un nouvel appel à
 * `client.setAuth(...)`, que `ConvexProviderWithAuth` ne refait QUE si
 * l'identité de `fetchAccessToken` change. L'écran de login réapparaît alors
 * que le cookie `dozka_session` est parfaitement valide, et rien ne remonte
 * tout seul jusqu'au rechargement de la page.
 *
 * Or Convex rappelle cette fonction toutes les ~5 min (10 s avant l'expiration
 * du JWT WorkOS) : sur un shift de 8 h ça fait ~100 occasions de tomber. Le
 * `catch { return null }` d'origine transformait donc n'importe quel hoquet —
 * un rollout de `francis-api` (12 le 21-08 en 112 min), un wifi qui saute
 * trois secondes, un laptop qui se réveille — en déconnexion sèche d'un
 * chatteur en plein shift. Symptôme : « je me fais déconnecter des fois comme
 * ça », et se reconnecter marche sans mot de passe (la session était vivante).
 *
 * D'où les deux garde-fous ci-dessous :
 *   1. seul un **401 franc** rend `null` (là, la session est vraiment morte) ;
 *      tout le reste est retenté avec backoff avant de rendre la main ;
 *   2. si on a fini par abandonner, on se **redonne une chance** quand le
 *      réseau revient ou que l'onglet reprend le focus, en changeant
 *      l'identité de `fetchAccessToken` — c'est le seul levier qui refait
 *      passer Convex par `setAuth`.
 */

/** Nombre total de tentatives pour UN appel de Convex (1 + 4 retries). */
const TENTATIVES_MAX = 5;
/** Backoff entre deux tentatives : 0.5s, 1s, 2s, 4s ≈ 7.5s de couverture,
 *  l'ordre de grandeur d'un rollout de pod ou d'un wifi qui repart. */
const BACKOFF_MS = [500, 1_000, 2_000, 4_000];
/** Timeout par tentative. Plus court que les 20 s par défaut du client : on
 *  préfère retenter vite que d'attendre un pod qui ne répondra pas. */
const TIMEOUT_TENTATIVE_MS = 8_000;

const attendre = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

/** Un 401 est la SEULE réponse qui dit « cette session n'existe plus ».
 *  Un 5xx, un timeout, une coupure réseau ne disent rien sur la session. */
function estSessionMorte(e: unknown): boolean {
  return e instanceof ApiError && e.status === 401;
}

export function useAuthBridge() {
  const { user, isLoading, getAccessToken } = useSession();

  // Bumpé quand on veut forcer Convex à refaire un `setAuth` (cf. §2 plus
  // haut). Change l'identité de `fetchAccessToken`, rien d'autre.
  const [reprise, setReprise] = useState(0);
  // `true` seulement si la dernière tentative a échoué sur un transitoire —
  // sans ça on relancerait une auth à chaque retour d'onglet, pour rien.
  const enEchecTransitoire = useRef(false);

  const fetchAccessToken = useCallback(
    async (
      _opts: { forceRefreshToken: boolean } = { forceRefreshToken: false },
    ): Promise<string | null> => {
      for (let tentative = 0; tentative < TENTATIVES_MAX; tentative++) {
        try {
          const token = await getAccessToken({
            timeout: TIMEOUT_TENTATIVE_MS,
          });
          enEchecTransitoire.current = false;
          return token;
        } catch (e) {
          if (estSessionMorte(e)) {
            // Vraie déconnexion : le cookie n'ouvre plus rien. On rend `null`,
            // Convex bascule sur <Unauthenticated>, et c'est CORRECT.
            enEchecTransitoire.current = false;
            return null;
          }
          const backoff = BACKOFF_MS[tentative];
          if (backoff === undefined) break; // plus de tentative : on abandonne
          await attendre(backoff);
        }
      }
      // Panne plus longue que notre fenêtre de retry. On rend `null` (on n'a
      // pas le choix, Convex attend un token), mais on note qu'on est en échec
      // TRANSITOIRE pour pouvoir se relancer dès que le monde revient.
      enEchecTransitoire.current = true;
      return null;
    },
    // `reprise` est une dépendance VOLONTAIRE et inutilisée dans le corps :
    // c'est elle qui change l'identité de la fonction, donc qui fait refaire
    // un `setAuth` à ConvexProviderWithAuth. Sans elle, pas de sortie de
    // `noAuth` sans rechargement de page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getAccessToken, reprise],
  );

  // Le réseau revient, ou le chatteur retourne sur l'onglet (laptop réveillé,
  // changement de fenêtre) : si le dernier essai avait échoué sur un
  // transitoire, on redonne sa chance à Convex.
  useEffect(() => {
    const relancer = () => {
      if (!enEchecTransitoire.current) return;
      if (typeof document !== "undefined" && document.hidden) return;
      enEchecTransitoire.current = false;
      setReprise((n) => n + 1);
    };
    window.addEventListener("online", relancer);
    document.addEventListener("visibilitychange", relancer);
    return () => {
      window.removeEventListener("online", relancer);
      document.removeEventListener("visibilitychange", relancer);
    };
  }, []);

  return useMemo(
    () => ({
      isLoading,
      isAuthenticated: user !== null,
      fetchAccessToken,
    }),
    [isLoading, user, fetchAccessToken],
  );
}
